import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";import { useAuth } from "@/lib/auth";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn } from "lucide-react";
import { BrandLogo, BRAND } from "@/components/BrandLogo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const u = await login(email, password);
      const next = loc.state?.from?.pathname || (u.role === "admin" ? "/admin" : "/dashboard");
      // Approved users → their target; otherwise router will redirect to /pending
      nav(u.status === "approved" ? next : "/pending", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{
        background:
          "radial-gradient(1200px 600px at 15% -10%, #f4ede3 0%, transparent 60%), " +
          "radial-gradient(900px 500px at 90% 100%, #eef4ea 0%, transparent 55%), " +
          "linear-gradient(180deg, #f7f4ee 0%, #efeae0 100%)",
      }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <BrandLogo size={30} />
        </div>
        <div className="border border-neutral-200 rounded-sm bg-white p-6 space-y-5 shadow-lg">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">// sign in</div>
            <h1 className="text-2xl font-bold tracking-tight mt-1 text-neutral-900">Welcome back</h1>
            <p className="text-sm text-neutral-600 mt-1">Warehouse & supermarket layout designer.</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="li-email">Email</Label>
              <Input
                id="li-email" data-testid="login-email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="li-password">Password</Label>
              <Input
                id="li-password" data-testid="login-password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive font-mono border border-destructive/30 bg-destructive/10 rounded-sm px-3 py-2">
                {error}
              </div>
            )}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-sm font-mono uppercase tracking-widest text-xs h-9 text-white hover:brightness-110"
              style={{ background: BRAND.red }}
            >
              <LogIn className="w-3.5 h-3.5 mr-1.5" />
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="border-t border-neutral-200 pt-4 text-xs text-neutral-500 font-mono">
            Accounts are created by your administrator. Contact them if you need access.
          </div>
        </div>
      </div>
    </div>
  );
}
